// Users (docs/cockpit/spec.md): everyone who works with LAMP in the business, their Roles, where they talk to
// LAMP, how they sign in, when they were last active and whether they have accepted their invite. An Owner
// invites people by email or mobile number, sends someone still invited the invitation again, and changes their Roles; the Application API
// checks each again, and asks for a step-up (ADR-0019) before Roles change or someone is made an Owner.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ApiError, type Api, type People, type PersonRow, type RoleName } from "../api.js";
import { locale } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { CATALOGUES, useLanguage, useWords, type Words } from "../i18n/words.js";
import { useStepUp } from "../step-up/StepUp.js";

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

type Filter = "everyone" | "active" | "invited";

/** When a person was last active, in the business's words: "Never", a time today, or a date. */
export function lastActive(iso: string | null, now: Date = new Date(), language: Language = "en"): string {
  if (!iso) return CATALOGUES[language].usersNever;
  const at = new Date(iso);
  const day = (d: Date) =>
    new Intl.DateTimeFormat(locale(language), { timeZone: "Asia/Kolkata", dateStyle: "medium" }).format(d);
  if (day(at) === day(now))
    return new Intl.DateTimeFormat(locale(language), { timeZone: "Asia/Kolkata", timeStyle: "short" }).format(at);
  return day(at);
}

function RolePicker({
  roles,
  chosen,
  onChange,
  legend,
}: {
  roles: readonly RoleName[];
  chosen: readonly string[];
  onChange: (roles: string[]) => void;
  legend: string;
}) {
  return (
    <fieldset className="role-picker">
      <legend>{legend}</legend>
      {roles.map((r) => (
        <label key={r.id}>
          <input
            type="checkbox"
            checked={chosen.includes(r.id)}
            onChange={(e) => onChange(e.target.checked ? [...chosen, r.id] : chosen.filter((id) => id !== r.id))}
          />{" "}
          {r.name}
        </label>
      ))}
    </fieldset>
  );
}

/** How an invitation reaches the person: an email link, or a code the Owner passes on for Telegram. */
type InviteBy = "email" | "mobile";

function InviteForm({
  roles,
  busy,
  onInvite,
}: {
  roles: readonly RoleName[];
  busy: boolean;
  onInvite: (input: { by: InviteBy; address: string; name: string; roles: string[] }) => Promise<boolean>;
}) {
  const w = useWords();
  const [by, setBy] = useState<InviteBy>("email");
  const [address, setAddress] = useState("");
  const [name, setName] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (await onInvite({ by, address, name, roles: chosen })) {
      setAddress("");
      setName("");
      setChosen([]);
    }
  };
  const label: Record<InviteBy, string> = { email: w.usersEmail, mobile: w.usersMobile };
  return (
    <form className="card" aria-label={w.usersInvite} onSubmit={(e) => void submit(e)} noValidate>
      <div className="seg" role="group" aria-label={w.usersInviteBy}>
        {(Object.keys(label) as InviteBy[]).map((b) => (
          <button
            key={b}
            type="button"
            aria-pressed={by === b}
            onClick={() => {
              setBy(b);
              setAddress("");
            }}
          >
            {label[b]}
          </button>
        ))}
      </div>
      <label className="field" htmlFor="invite-name">
        {w.usersName}
      </label>
      <input id="invite-name" value={name} autoComplete="off" onChange={(e) => setName(e.target.value)} />
      <label className="field" htmlFor={`invite-${by}`}>
        {label[by]}
      </label>
      <input
        id={`invite-${by}`}
        type={by === "email" ? "email" : "tel"}
        value={address}
        autoComplete="off"
        required
        onChange={(e) => setAddress(e.target.value)}
      />
      <RolePicker roles={roles} chosen={chosen} onChange={setChosen} legend={w.usersRoles} />
      <button type="submit" className="btn btn-go" disabled={busy || address.trim() === "" || chosen.length === 0}>
        {w.usersInvite}
      </button>
    </form>
  );
}

function PersonRoles({
  person,
  roles,
  busy,
  onSave,
}: {
  person: PersonRow;
  roles: readonly RoleName[];
  busy: boolean;
  onSave: (person: PersonRow, roles: string[]) => Promise<boolean>;
}) {
  const w = useWords();
  const [editing, setEditing] = useState(false);
  const [chosen, setChosen] = useState<string[]>([...person.roles]);
  const names = person.roles.map((id) => roles.find((r) => r.id === id)?.name ?? id).join(", ");
  const who = person.name ?? person.email ?? w.usersThisPerson;
  if (!editing)
    return (
      <>
        {names || "—"}{" "}
        <button
          type="button"
          className="btn btn-small"
          aria-label={w.usersChangeRolesOf(who)}
          onClick={() => {
            setChosen([...person.roles]);
            setEditing(true);
          }}
        >
          {w.usersChange}
        </button>
      </>
    );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(person, chosen).then((saved) => saved && setEditing(false));
      }}
    >
      <RolePicker roles={roles} chosen={chosen} onChange={setChosen} legend={w.usersRolesOf(who)} />
      <button type="submit" className="btn btn-go" disabled={busy || chosen.length === 0}>
        {w.usersSaveRoles}
      </button>{" "}
      <button type="button" className="btn" onClick={() => setEditing(false)}>
        {w.usersCancel}
      </button>
    </form>
  );
}

export function Users({ api, now }: { api: Api; now?: Date }) {
  const w = useWords();
  const { language } = useLanguage();
  const [people, setPeople] = useState<People | undefined>(undefined);
  const [filter, setFilter] = useState<Filter>("everyone");
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const stepUp = useStepUp(api.stepUp);

  const load = useCallback(() => {
    api.people().then(setPeople, (error: unknown) => setProblem(problemOf(error, w)));
  }, [api]);
  useEffect(load, [load]);

  const attempt = async (work: () => Promise<string>): Promise<boolean> => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      setNotice(await stepUp.run(work));
      load();
      return true;
    } catch (error) {
      setProblem(problemOf(error, w));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!people) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.usersLoading}</p>;
  const counts: Record<Filter, number> = {
    everyone: people.people.length,
    active: people.people.filter((p) => p.status === "active").length,
    invited: people.people.filter((p) => p.status === "invited").length,
  };
  const shown = people.people.filter((p) => filter === "everyone" || p.status === filter);
  // The Roles no one holds yet, and what waits on each: on the Owners' Today until someone does (#567).
  const unheld = people.unheld ?? [];
  const label: Record<Filter, string> = { everyone: w.usersEveryone, active: w.usersActive, invited: w.usersInvited };
  return (
    <>
      <p className="muted">{w.usersIntro}</p>
      {unheld.length > 0 ? (
        <section className="unheld" aria-label={w.usersNoOneHolds}>
          <p>{w.usersNoOneHolds}</p>
          <ul>
            {unheld.map((u) => (
              <li key={u.role}>
                {w.usersRoleWaiting(people.roles.find((r) => r.id === u.role)?.name ?? u.role, u.waiting)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      <div className="toolbar">
        <div className="seg" role="group" aria-label={w.usersShow}>
          {(Object.keys(label) as Filter[]).map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {label[f]} ({counts[f]})
            </button>
          ))}
        </div>
        <button type="button" className="btn btn-go" aria-expanded={inviting} onClick={() => setInviting(!inviting)}>
          {w.usersInvite}
        </button>
      </div>
      {inviting ? (
        <InviteForm
          roles={people.roles}
          busy={busy}
          onInvite={(input) =>
            attempt(async () => {
              const sent = await api.invite({
                ...(input.by === "email" ? { email: input.address } : { mobile: input.address }),
                roles: input.roles,
                ...(input.name.trim() ? { name: input.name } : {}),
              });
              setInviting(false);
              // A mobile invitation goes nowhere by itself: the Owner passes its code on.
              return sent.kind === "mobile"
                ? w.usersInvitedMobileNotice(input.name.trim() || input.address, input.address, sent.code)
                : w.usersInvitedNotice(input.address);
            })
          }
        />
      ) : null}
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th scope="col">{w.usersPerson}</th>
              <th scope="col">{w.usersRole}</th>
              <th scope="col">{w.usersTalksOn}</th>
              <th scope="col">{w.usersSignsInWith}</th>
              <th scope="col">{w.usersLastActive}</th>
              <th scope="col">{w.usersStatus}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id}>
                <td>
                  <strong>{p.name ?? p.email ?? "—"}</strong>
                  <br />
                  <span className="small muted">
                    {[p.name ? p.email : null, p.accountHolder ? w.usersAccountHolder : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </td>
                <td>
                  <PersonRoles
                    person={p}
                    roles={people.roles}
                    busy={busy}
                    onSave={(person, roles) =>
                      attempt(async () => {
                        await api.setRoles(person.id, roles);
                        return w.usersRolesChanged(person.name ?? person.email ?? w.usersThatPerson);
                      })
                    }
                  />
                </td>
                <td>{p.channels.join(", ") || "—"}</td>
                <td>{p.signInMethods.join(", ") || "—"}</td>
                <td>{p.status === "invited" ? "—" : lastActive(p.lastActive, now, language)}</td>
                <td>
                  {p.status === "active" ? (
                    <span className="tag tag-good">{w.usersActive}</span>
                  ) : (
                    <>
                      <span className="tag tag-plain">{w.usersInviteSent}</span>{" "}
                      <button
                        type="button"
                        className="btn btn-small"
                        disabled={busy}
                        aria-label={w.usersSendAgainTo(p.name ?? p.email ?? p.mobile ?? w.usersThisPerson)}
                        onClick={() =>
                          void attempt(async () => {
                            const sent = await api.reinvite(p.id);
                            const who = p.name ?? p.email ?? p.mobile ?? w.usersThatPerson;
                            // A mobile invitation goes nowhere by itself: the Owner passes its code on.
                            return sent.kind === "mobile"
                              ? w.usersSentAgainMobile(who, sent.code)
                              : w.usersSentAgain(who);
                          })
                        }
                      >
                        {w.usersSendAgain}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2 className="sectionh">{w.usersWhenInvited}</h2>
      <p className="muted">{w.usersWhenInvitedText}</p>
      {stepUp.dialog}
    </>
  );
}
